import { useState } from 'react';
import { useCreateFeatureFlag, useFeatureFlags, useToggleFeatureFlag } from '../useAdmin';
import type { FeatureFlagRow } from '../adminApi';
import { AuthApiError } from '../../account/authApi';
import './AdminFlags.css';

/**
 * Admin feature flags (F18). Persistent toggles the admin flips
 * without a redeploy. Create new flags inline; toggle existing
 * ones with a switch. All changes write audit rows.
 */
export function AdminFlags(): JSX.Element {
  const { data, isLoading, isError, error } = useFeatureFlags();
  const create = useCreateFeatureFlag();
  const toggle = useToggleFeatureFlag();

  const [newKey, setNewKey] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [newEnabled, setNewEnabled] = useState(false);

  const lastError = create.error || toggle.error;

  return (
    <section className="bl-admin-flags" aria-label="Feature flags">
      <header className="bl-admin-flags__header">
        <h2>Feature flags</h2>
        <p className="bl-admin-flags__sub">
          Persistent runtime toggles. Changes here propagate at the next request — no redeploy
          needed.
        </p>
      </header>

      {(isError || lastError) && (
        <p className="bl-admin-flags__error" role="alert">
          {lastError instanceof AuthApiError
            ? lastError.detail || lastError.message
            : (error || lastError) instanceof Error
              ? ((error || lastError) as Error).message
              : 'Could not complete the request.'}
        </p>
      )}

      <section className="bl-admin-flags__section">
        <h3>Create flag</h3>
        <form
          className="bl-admin-flags__create"
          onSubmit={(e) => {
            e.preventDefault();
            if (!newKey.trim()) return;
            create.mutate(
              {
                key: newKey.trim(),
                enabled: newEnabled,
                description: newDesc.trim() || undefined,
              },
              {
                onSuccess: () => {
                  setNewKey('');
                  setNewDesc('');
                  setNewEnabled(false);
                },
              },
            );
          }}
        >
          <input
            type="text"
            value={newKey}
            onChange={(e) => setNewKey(e.target.value)}
            placeholder="flag_key"
            aria-label="Flag key"
            spellCheck={false}
          />
          <input
            type="text"
            value={newDesc}
            onChange={(e) => setNewDesc(e.target.value)}
            placeholder="What does this control?"
            aria-label="Description"
          />
          <label className="bl-admin-flags__check">
            <input
              type="checkbox"
              checked={newEnabled}
              onChange={(e) => setNewEnabled(e.target.checked)}
            />
            <span>Enabled at create</span>
          </label>
          <button type="submit" disabled={!newKey.trim() || create.isPending}>
            {create.isPending ? 'Creating…' : 'Create'}
          </button>
        </form>
      </section>

      {isLoading ? (
        <p className="bl-admin-flags__notice">Loading flags…</p>
      ) : data?.rows.length === 0 ? (
        <p className="bl-admin-flags__notice">No flags yet. Create one above.</p>
      ) : (
        <ul className="bl-admin-flags__list">
          {data?.rows.map((row) => (
            <Entry
              key={row.id}
              row={row}
              onToggle={(enabled) => toggle.mutate({ id: row.id, enabled })}
              busy={toggle.isPending}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

function Entry({
  row,
  onToggle,
  busy,
}: {
  row: FeatureFlagRow;
  onToggle: (enabled: boolean) => void;
  busy: boolean;
}): JSX.Element {
  return (
    <li className="bl-admin-flags__entry" data-on={row.enabled || undefined}>
      <div>
        <p className="bl-admin-flags__key">
          <code>{row.key}</code>
        </p>
        {row.description && <p className="bl-admin-flags__desc">{row.description}</p>}
        <p className="bl-admin-flags__when">updated {formatDate(row.updated_at)}</p>
      </div>
      <label className="bl-admin-flags__switch">
        <input
          type="checkbox"
          checked={row.enabled}
          onChange={(e) => onToggle(e.target.checked)}
          disabled={busy}
          aria-label={`Toggle ${row.key}`}
        />
        <span className="bl-admin-flags__switch-label">{row.enabled ? 'On' : 'Off'}</span>
      </label>
    </li>
  );
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}
